import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Accessible label / dialog title shown in the header. */
  title: string;
  /** Optional descriptive line under the title. */
  description?: string;
  children: ReactNode;
  /** data-testid for the dialog element. */
  testId?: string;
  /**
   * - "default" — 520px centered card.
   * - "wide"    — capped ~1500px (legacy fixed-width).
   * - "fit"     — hugs its content up to a cap; for catalog grids that
   *               set their own column count so the dialog width adapts
   *               to how many items there are (no giant empty box).
   * All sizes scale with the display via --ui-scale.
   */
  size?: 'default' | 'wide' | 'fit';
}

/**
 * Glass-styled modal built on the native <dialog> element so we get a
 * proper top-layer render, focus trap, and ESC-to-close for free.
 *
 * Used by "Add classroom" today; reusable for any centered glass dialog.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  testId,
  size = 'default',
}: ModalProps) {
  const ref = useRef<globalThis.HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Native dialog dispatches `cancel` (ESC) and `close` (.close()).
  // Funnel both through onClose so React state stays in sync.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);
    return () => {
      dialog.removeEventListener('close', handleClose);
    };
  }, [onClose]);

  // Click on the backdrop (outside the inner card) closes.
  const onDialogClick = (event: MouseEvent<globalThis.HTMLDialogElement>) => {
    if (event.target === ref.current) onClose();
  };

  return (
    <dialog
      ref={ref}
      className={[
        styles.dialog,
        size === 'wide' ? styles.dialogWide : '',
        size === 'fit' ? styles.dialogFit : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onDialogClick}
      data-testid={testId ?? 'modal'}
      aria-label={title}
    >
      <div className={`glass glass--solid ${styles.card}`} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>{title}</h2>
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close"
            data-testid="modal-close"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              aria-hidden="true"
              focusable="false"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </header>
        <div className={styles.body}>{children}</div>
      </div>
    </dialog>
  );
}
